import { useEffect, type ReactNode } from 'react';
import { TXProvider } from '@transifex/react';
import type PostHog from 'posthog-react-native';

import { BiometricEnrollmentProvider } from '../biometric/BiometricEnrollmentProvider';
import { setRuntimeConfig, type LoginConfig } from '../config/runtimeConfig';
import { LoginMountProvider } from '../contexts/LoginMount';
import {
  OnAuthenticatedProvider,
  type OnAuthenticatedFn,
} from '../contexts/OnAuthenticated';
import { ThemePaletteProvider } from '../contexts/ThemePalette';
import type { Mode } from '../contexts/ThemePalette';
import { configureLoginTxToken, loginTx } from '../i18n/loginTx';
import { configureLoginPostHog, setHostPostHogClient } from '../utils/analytics/posthog';
import { getSystemLocale, getTransifexLocale } from '../utils/getSystemLocale';
import { AnalyticsProvider } from '../utils/analytics';

type BaseLoginRootProps = {
  mountBase?: string;
  themeOverride?: Mode;
  // Absent → login falls back to `window.location.href` on native.
  onAuthenticated?: OnAuthenticatedFn;
  children: ReactNode;
};

// Discriminated: under `embedded: true` the host MUST provide `posthogClient`.
// Without it, login would fall back to constructing its own client (or throw
// on missing PostHog config), and variantFlags: undefined → posthogGetFeatureFlag
// on a null client → silent misfire.
type LoginRootProps =
  | (BaseLoginRootProps & {
      config?: Extract<LoginConfig, { embedded?: false }>;
      posthogClient?: never;
    })
  | (BaseLoginRootProps & {
      config: Extract<LoginConfig, { embedded: true }>;
      posthogClient: PostHog;
    });

export const LoginRoot = ({
  mountBase = '/',
  themeOverride = 'light',
  onAuthenticated,
  config,
  posthogClient,
  children,
}: LoginRootProps) => {
  // Publish synchronously during render — a useEffect would fire after
  // children commit and boot-time requests would hit the API before the
  // override lands.
  if (config) setRuntimeConfig(config);
  configureLoginTxToken();

  // Gate on config.embedded, not on posthogClient truthiness. A host that
  // renders <LoginRoot posthogClient={maybeNull!} /> before its client is
  // ready would otherwise fall into configureLoginPostHog() → requireBool
  // on EXPO_PUBLIC_POSTHOG_ENABLED (not inlined inside node_modules) → throw.
  if (config?.embedded) {
    if (posthogClient) setHostPostHogClient(posthogClient);
  } else {
    configureLoginPostHog();
  }

  useEffect(() => {
    const locale = getTransifexLocale(getSystemLocale());
    if (locale !== 'en') {
      loginTx.setCurrentLocale(locale).catch(() => {
        /* offline / CDS error — falls through to source strings */
      });
    }
  }, []);

  return (
    <AnalyticsProvider>
      <ThemePaletteProvider override={themeOverride}>
        <LoginMountProvider mountBase={mountBase}>
          <OnAuthenticatedProvider onAuthenticated={onAuthenticated}>
            {/* Scope useT() to login's Transifex instance. */}
            <TXProvider instance={loginTx}>
              <BiometricEnrollmentProvider>{children}</BiometricEnrollmentProvider>
            </TXProvider>
          </OnAuthenticatedProvider>
        </LoginMountProvider>
      </ThemePaletteProvider>
    </AnalyticsProvider>
  );
};
